import { Button, Checkbox, Dialog, DialogActions, DialogContent, MenuItem, TextField } from "@mui/material";
import { Component } from "react";
import DialogTitleWithCloseButton from "./DialogTitleWithCloseButton";
import _ from 'lodash';
import PropTypes from 'prop-types';

/**
 * @callback OnCloseCallback
 * @param {object|undefined} filters
 * @returns void
 */

/**
 * @callback ShouldDisplayFilterCallback
 * @param {object} filters
 * @returns {boolean}
 */

/**
 * @typedef {object} Filter
 * @property {string} field
 * @property {string} label
 * @property {*} value
 * @property {"string"|"number"|"date"|"boolean"|"select"|"custom"} type
 * @property {string[]|{value:string,label:String}[]} options
 * @property {function|class|undefined} component
 * @property {ShouldDisplayFilterCallback|undefined} shouldDisplay
 */

/**
 * @augments {Component<Props, State>}
 */
export default class Filters extends Component {

   static propTypes = {
      onClose: PropTypes.func.isRequired,
      filters: PropTypes.arrayOf(PropTypes.shape({
         field: PropTypes.string.isRequired,
         label: PropTypes.string.isRequired,
         value: PropTypes.any,
         type: PropTypes.oneOf(['string', 'number', 'date', 'boolean', 'select', 'custom']).isRequired,
         options: PropTypes.array,
         component: PropTypes.func,
         shouldDisplay: PropTypes,
      })).isRequired,
   }

   state = {
      filters: {}
   }

   updateFilters = (newFilters) => {
      this.setState({ filters: { ...this.state.filters, ...newFilters } });
   }

   save = () => {
      // set empty values to undefined
      const res = {};
      for (const field in this.state.filters) {
         const value = this.state.filters[field];
         if (typeof value === 'boolean') // in this case, a falsy value is valid
            res[field] = value;
         else
            res[field] = value || undefined;
      }

      // check if filters changed
      const changed = this.props.filters.some(filter => !_.isEqual(res[filter.field], filter.value));
      if (!changed)
         return this.props.onClose();

      this.props.onClose(res);
   }

   componentDidUpdate(_, prevState) {
      if (prevState.filters !== this.state.filters) {
         
         // list filters that were displaying but are not anymore
         const filtersToSetEmpty = this.props.filters.filter(filter => {
            const { shouldDisplay=()=>true } = filter;
            return shouldDisplay(prevState.filters) && !shouldDisplay(this.state.filters);
         });

         // set empty values to undefined
         const updates = filtersToSetEmpty.reduce((acc, filter) => {
            acc[filter.field] = undefined;
            return acc;
         }, {});

         if (Object.keys(updates).length > 0)
            this.updateFilters(updates);
      }

   }

   componentDidMount() {
      const filters = this.props.filters.reduce((acc, filter) => {
         const { value } = filter;

         if (typeof value === 'boolean') // in this case, a falsy value is valid
            acc[filter.field] = value;
         else
            acc[filter.field] = value || '';

         return acc;
      }, {});

      this.setState({ filters });
   }

   render() {
      const variant = 'filled';
      const { filters } = this.state;

      return <Dialog open>
         <DialogTitleWithCloseButton onClose={this.props.onClose}>
            Filters
         </DialogTitleWithCloseButton>
         <DialogContent dividers>
            <div className="max-w-full w-[250px] overflow-x-hidden overflow-y-auto [&>*]:my-4">
               {
                  this.props.filters.map(filter => {

                     const { type, field, label, shouldDisplay=()=>true } = filter;

                     if (!shouldDisplay(filters))
                        return null;

                     switch (type) {
                        case 'string':
                           return <TextField
                              label={label}
                              value={filters[field]}
                              onChange={e => this.updateFilters({ [field]: e.target.value })}
                              size="small"
                              variant={variant}
                              fullWidth
                           />
                        case 'number':
                           return <TextField
                              label={label}
                              value={filters[field]}
                              onChange={e => this.updateFilters({ [field]: e.target.value })}
                              size="small"
                              variant={variant}
                              fullWidth
                              type="number"
                           />
                        case 'date':
                           return <TextField
                              label={label}
                              value={filters[field]}
                              onChange={e => this.updateFilters({ [field]: e.target.value })}
                              size="small"
                              variant={variant}
                              fullWidth
                              type="date"
                              slotProps={{
                                 inputLabel: {
                                    shrink: true,
                                 }
                              }}
                           />
                        case 'boolean':
                           return <div
                              className="bg-gray-200 py-1 flex items-center cursor-pointer inline-block border-b-[1px] border-b-gray-400"
                              onClick={() => {
                                 const oldValue = filters[field];
                                 let newValue;

                                 if (oldValue === true)
                                    newValue = false;
                                 else if (oldValue === false)
                                    newValue = '';
                                 else
                                    newValue = true;

                                 this.updateFilters({ [field]: newValue });
                              }}
                           >
                              <Checkbox
                                 label={label}
                                 size="small"
                                 fullWidth
                                 checked={filters[field] || false}
                                 indeterminate={typeof filters[field] !== 'boolean'}
                              />
                              <span className="text-gray-600">{label}</span>
                           </div>
                        case 'select':
                           return <TextField
                              label={label}
                              value={filters[field] || ''}
                              onChange={e => this.updateFilters({ [field]: e.target.value })}
                              size="small"
                              variant={variant}
                              fullWidth
                              select
                              slotProps={{
                                 select: {
                                    displayEmpty: true,
                                 },
                                 inputLabel: {
                                    shrink: true,
                                 }
                              }}
                           >
                              <MenuItem value="">
                                 All
                              </MenuItem>
                              {
                                 filter.options.map(option => <MenuItem
                                       key={option.value || option}
                                       value={option.value || option}
                                    >
                                       {option.label || option}
                                    </MenuItem>
                                 )
                              }
                           </TextField>
                        case 'custom':
                        default:
                           return <filter.component
                              label={label}
                              value={filters[field]}
                              onChange={value => this.updateFilters({ [field]: value })}
                              size="small"
                              variant={variant}
                              fullWidth
                           />
                     }

                  })
               }
            </div>
         </DialogContent>
         <DialogActions>
            <Button onClick={this.save}>
               SAVE
            </Button>
         </DialogActions>
      </Dialog>
   }
      
}